import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

// Base64url: no padding, URL-safe.
function toBase64Url(str: string): string {
  const encoded = btoa(unescape(encodeURIComponent(str)));
  return encoded.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(b64url: string): string {
  let b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
  while (b64.length % 4) b64 += "=";
  return decodeURIComponent(escape(atob(b64)));
}

// A link into another utility with its controls already answered, written from outside the page it addresses: the
// fragment is the one that page would have shared, so its own decoders are what read it back.
export function shareLink(path: string, state: Record<string, unknown>): string {
  return `${path}#${toBase64Url(JSON.stringify(state))}`;
}

type GetStateFn = () => Record<string, unknown> | null;

interface ShareStateContextValue {
  registerGetState: (fn: GetStateFn) => void;
  unregisterGetState: () => void;
  getShareUrl: () => string | null;
  syncHash: () => void;
  clearHash: () => void;
}

const ShareStateContext = createContext<ShareStateContextValue | null>(null);

// Writes trail the edits: one per keystroke would hit Safari's replaceState limit of ~100 per 30 seconds.
const HASH_SYNC_DELAY = 250;

// Past this the URL stops following: redrawing a megabyte-long address bar reads as the editor seizing up.
const MAX_TRACKED_HASH_LENGTH = 64 * 1024;

export function ShareStateProvider({ children }: { children: ReactNode }) {
  const getStateFnRef = useRef<GetStateFn | null>(null);
  // What the utility mounted with: until the state moves off it, the URL is left clean.
  const baselineRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A pasted link changes only the fragment, so nothing remounts; reloading replays startup with the new hash.
  useEffect(() => {
    const handleHashChange = () => {
      if (decodeHashState(window.location.hash)) window.location.reload();
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const cancelPendingWrite = useCallback(() => {
    if (timerRef.current === null) return;
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const registerGetState = useCallback((fn: GetStateFn) => {
    getStateFnRef.current = fn;
    baselineRef.current = encodeState(fn());
  }, []);

  const unregisterGetState = useCallback(() => {
    // Navigating away drops the fragment, so a queued write would otherwise land on the next utility's URL.
    cancelPendingWrite();
    getStateFnRef.current = null;
    baselineRef.current = null;
  }, [cancelPendingWrite]);

  const writeHash = useCallback(() => {
    const encoded = encodeState(getStateFnRef.current?.() ?? null);
    if (encoded === null || encoded.length > MAX_TRACKED_HASH_LENGTH) return;
    const current = window.location.hash.slice(1);
    if (current === encoded) return;
    if (!current && encoded === baselineRef.current) return;
    history.replaceState(history.state, "", urlWithHash(encoded));
  }, []);

  const syncHash = useCallback(() => {
    cancelPendingWrite();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      writeHash();
    }, HASH_SYNC_DELAY);
  }, [cancelPendingWrite, writeHash]);

  useEffect(() => cancelPendingWrite, [cancelPendingWrite]);

  const getShareUrl = useCallback((): string | null => {
    const encoded = encodeState(getStateFnRef.current?.() ?? null);
    if (encoded === null) return null;
    // Settle the address bar on the link that goes to the clipboard, even for a utility nobody has touched.
    cancelPendingWrite();
    const url = urlWithHash(encoded);
    const displayable = encoded.length <= MAX_TRACKED_HASH_LENGTH;
    if (displayable && window.location.hash.slice(1) !== encoded) {
      history.replaceState(history.state, "", url);
    }
    return url;
  }, [cancelPendingWrite]);

  // A reset takes the fragment off, and re-baselines on what is there so the URL waits for the next real change.
  const clearHash = useCallback(() => {
    cancelPendingWrite();
    baselineRef.current = encodeState(getStateFnRef.current?.() ?? null);
    const url = new URL(window.location.href);
    url.hash = "";
    history.replaceState(history.state, "", url.toString());
  }, [cancelPendingWrite]);

  const value = useMemo(
    () => ({ registerGetState, unregisterGetState, getShareUrl, syncHash, clearHash }),
    [registerGetState, unregisterGetState, getShareUrl, syncHash, clearHash],
  );

  return <ShareStateContext.Provider value={value}>{children}</ShareStateContext.Provider>;
}

function urlWithHash(encoded: string): string {
  const url = new URL(window.location.href);
  url.hash = encoded;
  return url.toString();
}

// A state JSON cannot represent costs the share link only — never the render or the keystroke that caused it.
function encodeState(state: Record<string, unknown> | null): string | null {
  if (!state) return null;
  try {
    // Undefined is how a utility says a field has no bearing on the view it is showing; JSON.stringify drops those.
    return toBase64Url(JSON.stringify(state));
  } catch {
    return null;
  }
}

export function useShareStateContext() {
  return useContext(ShareStateContext);
}

export function useInitialHashState<T extends Record<string, unknown>>(): T | null {
  // Read once and left in place: React can throw a render away, and the retry has to see the same hash.
  const [initialState] = useState<T | null>(() => decodeHashState<T>(window.location.hash));

  return initialState;
}

function decodeHashState<T>(hash: string): T | null {
  const payload = hash.slice(1);
  if (!payload) return null;
  try {
    return JSON.parse(fromBase64Url(payload)) as T;
  } catch {
    return null;
  }
}

// A field the view the utility is showing has no use for is handed back as undefined, which keeps it out of the link.
// The ref lets the callback read current values without re-registering.
export function useRegisterShareState(getState: () => Record<string, unknown>) {
  const ctx = useShareStateContext();
  const getStateRef = useRef(getState);
  getStateRef.current = getState;

  useEffect(() => {
    if (!ctx) return;
    ctx.registerGetState(() => getStateRef.current());
    return () => ctx.unregisterGetState();
  }, [ctx]);

  const syncShareState = useCallback(() => ctx?.syncHash(), [ctx]);

  // React state reaches the URL on its own; a value that lives outside a render calls the returned function.
  useEffect(() => {
    syncShareState();
  });

  return syncShareState;
}
